import React, { useCallback, useMemo, type ComponentType } from 'react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { isLoaderErrorAttributes } from '@jira/platform__react-loosely-lazy-error-handling/src/is-loader-error-attributes.tsx';
import { GroupByDropdown as GroupByDropdownItem } from '@atlassian/jira-issue-table-group-by-dropdown/src/index.tsx';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { HideDoneItemsToggle } from '@atlassian/jira-issue-table-hide-done-toggle/src/index.tsx';
import { useIssueTableSelectedIssuesActions } from '@atlassian/jira-native-issue-table/src/controllers/selected-issues/index.tsx';
import { SaveResetViewSettingsButton } from '@atlassian/jira-issue-navigator-save-reset-view-settings-button/src/ui/SaveResetViewSettingsButton.tsx';
import { PACKAGE_NAME, TEAM_NAME, isJiraSpreadsheetView } from '../../common/constants.tsx';
import type { CustomHeaderProps } from '../../common/types.tsx';
import { HierarchyToggleItem } from '../../common/ui/hierarchy-toggle/index.tsx';
import { withReportErrors } from '../../common/ui/with-report-errors/index.tsx';
import { useSelectedViewState } from '../../controllers/selected-view-state/index.tsx';
import { useActiveJql, useResolvedJql } from '../../services/active-jql/index.tsx';
import { useFilterQuery } from '../../services/filter-query/index.tsx';
import { useIssueSearchActions } from '../../services/issue-search/selectors.tsx';
import { useUpdateUrlQueryParamCallback } from '../../common/utils/spa-utils.tsx';
import type { main_issueNavigator_Header_jiraViewResult$key as JiraViewResultFragment } from './__generated__/main_issueNavigator_Header_jiraViewResult.graphql';
import type {
	main_issueNavigator_Header_issueResults$key as IssueResultsFragment,
	main_issueNavigator_Header_issueResults$data as IssueResultsData,
} from './__generated__/main_issueNavigator_Header_issueResults.graphql';
import type { main_issueNavigator_Header_filter$key as FilterFragment } from './__generated__/main_issueNavigator_Header_filter.graphql';

export type HeaderProps = {
	issueResults: IssueResultsFragment | null;
	CustomHeader: ComponentType<CustomHeaderProps>;
	filter: FilterFragment | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HeaderSkeletonImage: ComponentType<Record<any, any>>;
	jiraViewResult: JiraViewResultFragment | null;
	isGroupingSupported?: boolean;
	onChangeFilter?: (filterId?: string) => void;
};

const getLastPage = (
	pageCursors: IssueResultsData['pageCursors'] | undefined,
): number | null | undefined => {
	if (pageCursors?.last) {
		return pageCursors.last.pageNumber;
	}

	if (pageCursors?.around?.length) {
		return pageCursors.around[pageCursors.around.length - 1]?.pageNumber;
	}

	return undefined;
};

const Header = ({
	CustomHeader,
	filter,
	issueResults,
	HeaderSkeletonImage,
	jiraViewResult,
	isGroupingSupported,
	onChangeFilter,
}: HeaderProps) => {
	const issueResultsData = useFragment<IssueResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_Header_issueResults on JiraIssueConnection {
				issueNavigatorPageInfo {
					firstIssuePosition
				}
				pageCursors(maxCursors: 7) {
					around {
						pageNumber
					}
					last {
						pageNumber
					}
				}
				totalIssueSearchResultCount
			}
		`,
		issueResults,
	);
	// Compute page data for our search results which is needed when requesting items for the legacy meatball menu
	const offset = (issueResultsData?.issueNavigatorPageInfo?.firstIssuePosition ?? 1) - 1; // offset is 0 based so subtract 1
	const pages = getLastPage(issueResultsData?.pageCursors) ?? 0;
	const total = issueResultsData?.totalIssueSearchResultCount ?? 0;

	const searchResultPageData = useMemo(
		() => ({
			offset,
			pages,
			total,
		}),
		[offset, pages, total],
	);

	const { filterId } = useActiveJql();
	const jql = useResolvedJql();
	const { filterQueryIsFetching } = useFilterQuery();

	const filterData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_Header_filter on JiraFilter {
				filterId
				name
				isFavourite
				jql
			}
		`,
		filter,
	);
	const jiraViewData = useFragment<JiraViewResultFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_Header_jiraViewResult on JiraView
			@argumentDefinitions(
				isSavedListViewEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-enabled.relayprovider"
				}
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
			) {
				__typename
				... on JiraSpreadsheetView {
					...hierarchyToggle_issueNavigator_HierarchyToggleItem
					...GroupByDropdown
					...src_issueTableHideDoneToggle_HideDoneItemsToggle
						@arguments(isSavedViewsEnabled: $isSavedListViewTechnicalChangesEnabled)
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						hideDone
					}
				}
				... on JiraIssueSearchViewMetadata {
					...SaveResetViewSettingsButton @include(if: $isSavedListViewEnabled)
				}
			}
		`,
		jiraViewResult,
	);

	const filterDetails = useMemo(
		() =>
			filterData
				? {
						filterId: filterData.filterId,
						name: filterData.name,
						jql: filterData.jql,
						isFavourite: Boolean(filterData.isFavourite),
					}
				: undefined,
		[filterData],
	);
	const [{ view }] = useSelectedViewState();

	const jiraViewResultData = jiraViewData;

	let changeHierarchy = noop;
	let changeGroupedByField = noop;
	let changeHideDone = noop;
	let resetView = noop;
	if (fg('jsc_m2_hierarchy_fe_changes') || expVal('saved_list_view', 'isEnabled', false)) {
		({ changeHierarchy, changeGroupedByField, changeHideDone, resetView } =
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIssueSearchActions());
	}

	const updateGroupByParam = useUpdateUrlQueryParamCallback('groupBy');
	const onChangeGroupedByField = useCallback(
		(nextGroupedByField: string | null | undefined) => {
			changeGroupedByField(nextGroupedByField);
			updateGroupByParam(nextGroupedByField ?? undefined);
		},
		[changeGroupedByField, updateGroupByParam],
	);

	let hideDone = false;
	if (fg('jsc_bulk_edit_hide_done_work_items')) {
		hideDone = jiraViewData?.viewSettings?.hideDone ?? false;
	}

	const HierarchyToggle =
		jiraViewResultData && view !== 'detail' ? (
			<HierarchyToggleItem
				isGroupingSupported={isGroupingSupported}
				viewResult={jiraViewResultData}
				onChangeHierarchy={changeHierarchy}
			/>
		) : null;

	let onResetSelectedIssues = noop;
	if (fg('jsc_list_hide_done_items')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ reset: onResetSelectedIssues } = useIssueTableSelectedIssuesActions());
	}

	const HideDoneToggle =
		jiraViewResultData && view !== 'detail' && fg('jsc_list_hide_done_items') ? (
			<HideDoneItemsToggle
				isSavedViewsEnabled={fg('saved_list_view_technical_changes')}
				viewResult={jiraViewResultData}
				onChangeHideDone={changeHideDone}
				onResetSelectedIssues={onResetSelectedIssues}
			/>
		) : null;

	const GroupByDropdown =
		isGroupingSupported &&
		jiraViewData &&
		isJiraSpreadsheetView(jiraViewData) &&
		view !== 'detail' ? (
			<GroupByDropdownItem
				onChangeGroupedByField={onChangeGroupedByField}
				spreadsheetView={jiraViewData}
			/>
		) : null;

	const saveResetViewSettingsButton = jiraViewResultData &&
		expVal('saved_list_view', 'isEnabled', false) && (
			<SaveResetViewSettingsButton
				jql={jql}
				onReset={resetView}
				viewFragment={jiraViewResultData}
			/>
		);

	if (filterQueryIsFetching) {
		return <HeaderSkeletonImage />;
	}

	return (
		<UFOLabel name="header">
			<CustomHeader
				jql={jql}
				filterId={filterId}
				filterDetails={filterDetails}
				searchResultPageData={searchResultPageData}
				HierarchyToggle={HierarchyToggle}
				GroupByDropdown={GroupByDropdown}
				HideDoneToggle={HideDoneToggle}
				SaveResetViewSettingsButton={saveResetViewSettingsButton}
				isHideDoneWorkItemsEnabled={hideDone}
				onChangeFilter={onChangeFilter}
				isModifiedFilter={filterDetails?.jql !== jql}
			/>
		</UFOLabel>
	);
};

export default withReportErrors<HeaderProps>(Header, {
	id: 'ui.header.unhandled',
	packageName: PACKAGE_NAME,
	teamName: TEAM_NAME,
	sendToPrivacyUnsafeSplunk: true,
	attributes: isLoaderErrorAttributes,
});
